import { useEffect, useMemo, useState } from 'react';
import type { Account } from '../../core/accounts';
import { formatDateKey, todayKey } from '../../core/dates';
import { formatPaise, parseAmount } from '../../core/money';
import {
  convertible,
  isOverdue,
  outstanding,
  sortTasks,
  totalOutstandingPaise,
  type Task,
} from '../../core/tasks';
import { watchAccounts } from '../../data/accounts';
import type { UserRecord } from '../../data/auth';
import { withDefaults } from '../../data/preferences';
import { convertToTransaction, deleteTask, saveTask, setDone, watchTasks } from '../../data/tasks';

interface Props {
  record: UserRecord;
}

/**
 * Tasks (FR-TSK-01 to FR-TSK-04).
 *
 * Generalises the workbook's commitments block and its loose column-F note lists. Most
 * of the screen is an ordinary list; the part that earns its place is the one-tap convert
 * (FR-TSK-02), which is what makes this part of the books rather than a notes app.
 */
export function TasksScreen({ record }: Props) {
  const today = todayKey();
  const prefs = withDefaults(record.preferences);

  const [tasks, setTasks] = useState<Task[] | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [showDone, setShowDone] = useState(false);

  const [text, setText] = useState('');
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState('');

  const [converting, setConverting] = useState<Task | null>(null);
  const [convertAmount, setConvertAmount] = useState('');
  const [convertAccount, setConvertAccount] = useState('');

  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => watchTasks(setTasks), []);
  useEffect(() => watchAccounts(setAccounts), []);

  const accountType = useMemo(() => {
    const map = new Map(accounts.map((a) => [a.id, a.type]));
    return (id: string) => map.get(id);
  }, [accounts]);

  const visible = useMemo(
    () => sortTasks((tasks ?? []).filter((t) => showDone || !t.done)),
    [tasks, showDone],
  );

  async function add() {
    setError(null);
    const paise = amount.trim() ? parseAmount(amount) : null;
    if (amount.trim() && paise === null) {
      setError('That amount could not be read.');
      return;
    }

    setBusy(true);
    try {
      await saveTask(
        { text, amountPaise: paise, dueDateKey: dueDate || null },
        tasks ?? [],
        record.uid,
      );
      setText('');
      setAmount('');
      setDueDate('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not add that.');
    } finally {
      setBusy(false);
    }
  }

  function startConvert(task: Task) {
    setConverting(task);
    setConvertAmount(((task.amountPaise ?? 0) / 100).toFixed(2));
    setConvertAccount(prefs.defaultAccountId ?? accounts[0]?.id ?? '');
    setError(null);
  }

  async function convert() {
    if (!converting) return;
    setError(null);

    const paise = parseAmount(convertAmount);
    if (paise === null || paise <= 0) {
      setError('Enter an amount.');
      return;
    }
    if (!convertAccount) {
      setError('Choose an account.');
      return;
    }

    setBusy(true);
    try {
      await convertToTransaction(
        converting,
        { accountId: convertAccount, dateKey: today, amountPaise: paise },
        record.uid,
        accountType,
      );
      setNotice(`Posted "${converting.text}" as ${formatPaise(paise)}.`);
      setConverting(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not post that.');
    } finally {
      setBusy(false);
    }
  }

  if (tasks === null) return <p className="muted">Loading…</p>;

  const open = outstanding(tasks);

  return (
    <>
      <div className="head-row">
        <h1>Tasks</h1>
        <label className="inline">
          <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} />
          Show completed
        </label>
      </div>

      {error && <p className="error">{error}</p>}
      {notice && <p className="ok">{notice}</p>}

      {open.length > 0 && (
        <div className="figures">
          <div className="figure">
            <div className="figure-label">Outstanding</div>
            <div className="figure-value">{open.length}</div>
            <div className="muted tiny">{convertible(tasks).length} carry an amount</div>
          </div>
          <div className="figure">
            <div className="figure-label">Committed</div>
            <div className="figure-value">{formatPaise(totalOutstandingPaise(tasks))}</div>
            {/* A bare note counts as nothing — half of these have no amount at all. */}
            <div className="muted tiny">across the tasks that carry one</div>
          </div>
        </div>
      )}

      <section className="panel">
        <h2>Add a task</h2>
        <p className="muted tiny">
          Only the note is required. An amount and a due date are both optional
          (FR-TSK-01) — the workbook's note lists carry neither.
        </p>
        <div className="row">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="What needs doing"
            aria-label="Task"
          />
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Amount (optional)"
            aria-label="Amount"
          />
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            aria-label="Due date"
          />
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !text.trim()}
            onClick={() => void add()}
          >
            Add
          </button>
        </div>
      </section>

      {visible.length === 0 ? (
        <div className="placeholder">Nothing on the list.</div>
      ) : (
        <div className="table-scroll">
          <table className="table reclassify-table">
            <tbody>
              {visible.map((task) => (
                <tr key={task.id} className={task.done ? 'archived' : undefined}>
                  <td className="pick">
                    <input
                      type="checkbox"
                      checked={task.done}
                      onChange={(e) => void setDone(task, e.target.checked, record.uid)}
                      aria-label={`Complete ${task.text}`}
                    />
                  </td>
                  <td>
                    {task.text}
                    {task.createdFrom === 'workbook' && <span className="badge">workbook</span>}
                    {task.transactionId && <span className="badge">posted</span>}
                  </td>
                  <td>
                    {task.dueDateKey ? (
                      <>
                        {formatDateKey(task.dueDateKey, 'dd MMM yyyy')}
                        {isOverdue(task, today) && <span className="badge warn-badge">overdue</span>}
                      </>
                    ) : (
                      <span className="muted tiny">no date</span>
                    )}
                  </td>
                  <td className="num">
                    {task.amountPaise === null ? (
                      <span className="muted tiny">—</span>
                    ) : (
                      formatPaise(task.amountPaise, { symbol: false })
                    )}
                  </td>
                  <td className="actions">
                    {/* FR-TSK-02: one tap, pre-filled, still editable. */}
                    {task.amountPaise !== null && !task.transactionId && !task.done && (
                      <button type="button" className="btn-link" onClick={() => startConvert(task)}>
                        Post it
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn-link danger"
                      onClick={() => void deleteTask(task, record.uid)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {converting && (
        <section className="panel">
          <h2>Post “{converting.text}”</h2>
          <p className="muted tiny">
            Pre-filled from the task and still editable — a task is an intention written
            down in advance, so its figure is an estimate.
          </p>
          <div className="row">
            <input
              type="text"
              inputMode="decimal"
              value={convertAmount}
              onChange={(e) => setConvertAmount(e.target.value)}
              aria-label="Amount to post"
            />
            <select
              value={convertAccount}
              onChange={(e) => setConvertAccount(e.target.value)}
              aria-label="Account"
            >
              <option value="">Choose an account…</option>
              {accounts.filter((a) => !a.archived).map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void convert()}>
              {busy ? 'Posting…' : 'Post'}
            </button>
            <button type="button" className="btn btn-quiet" onClick={() => setConverting(null)}>
              Cancel
            </button>
          </div>
        </section>
      )}
    </>
  );
}
